import type { ComponentProps, ReactNode } from "react";

import { t } from "../contract/strings";
import { cx } from "./cx";

/**
 * The surface every grouped thing sits on.
 *
 * No shadow: this design's depth model is the technical outline -- a 1px border in the
 * accent at low opacity over a tonal step up from the page. Border *colour* and background
 * are not in here even though almost every card wants the defaults: `StageCard` recolours
 * both while recording, and a shared utility a variant overrides is decided by Tailwind's
 * emit order rather than by the caller. See `Button`.
 */
const CARD = "border rounded-card";
const CARD_TONE = "bg-surface border-line";

export interface CardProps extends ComponentProps<"div"> {
  /** Omit the default padding, margin and tone when a variant supplies its own. */
  bare?: boolean;
}

export function Card({ bare, className, ...rest }: CardProps) {
  return <div className={cx(CARD, !bare && cx(CARD_TONE, "p-pad my-4"), className)} {...rest} />;
}

export interface WhoCardProps extends Omit<ComponentProps<"div">, "children"> {
  name: ReactNode;
  /** `wrap-anywhere` where the name is an email address: they have nothing to break at. */
  nameClassName?: string;
  detail?: ReactNode;
  /** The action beside it -- "Not you?", "Sign out", or the Account/Diagnostics pair. */
  action?: ReactNode;
}

/**
 * Who this device thinks you are, and the way out of that.
 *
 * The two-child shape is the layout: an identity block that grows and one control pinned to
 * the end. It appears on enrolment (whose account this template is being built for), on
 * home, and on the signed-in account screen -- all three needing the same "and if that is
 * wrong, here" escape.
 */
export function WhoCard({ name, detail, action, nameClassName, className, ...rest }: WhoCardProps) {
  return (
    <Card className={cx("flex items-center justify-between gap-4", className)} {...rest}>
      <div>
        {/* The identity line stays in the body face and sentence case -- it is usually an
            email address, which mono caps would shout and misrepresent. */}
        <strong className={cx("block text-who font-semibold", nameClassName)}>{name}</strong>
        {detail === undefined ? null : <span className="text-note text-ink-soft">{detail}</span>}
      </div>
      {action}
    </Card>
  );
}

export interface CodeCardProps extends Omit<ComponentProps<"div">, "children"> {
  label?: ReactNode;
  code: ReactNode;
  codeId?: string;
  action?: ReactNode;
  /**
   * A sentence under the code that says what it is for. The code is read across a
   * table; the note is read by the one person holding this device, quietly, in body
   * type -- which is why it sits outside the display-size block above it.
   */
  note?: ReactNode;
}

/**
 * The session code is what gets read across a table, so it is the largest thing in the app:
 * a boarding-pass panel, code first, beacon toggle beneath.
 *
 * The action stretches to the card's width -- it is the only thing under the code and a
 * shrink-to-fit pill there reads as a stray control. Targeted at `> button` rather than at
 * every child, because the block above it is centred and must stay its own width.
 */
export function CodeCard({ label = t("ui.session_code"), code, codeId, action, note, className, ...rest }: CodeCardProps) {
  return (
    <Card
      bare
      className={cx(
        CARD_TONE,
        "my-4 flex flex-col items-center gap-panel text-center py-code-y px-pad",
        "[&>button]:self-stretch",
        className
      )}
      {...rest}
    >
      <div>
        <span className="font-code text-label font-semibold uppercase tracking-label text-ink-soft">
          {label}
        </span>
        <div
          id={codeId}
          className="font-code text-code font-bold tracking-code retrack-code tabular-nums"
        >
          {code}
        </div>
      </div>
      {note === undefined ? null : (
        <p className="m-0 max-w-[90%] text-note leading-row text-balance text-ink-soft">{note}</p>
      )}
      {action}
    </Card>
  );
}

export interface StageCardProps extends Omit<ComponentProps<"div">, "children"> {
  /** Progress at a glance -- the pips. */
  head?: ReactNode;
  /** The right-hand word in the head. Counting down, it becomes the seconds left. */
  count?: ReactNode;
  live?: boolean;
  /** The line to read. aria-live because it changes between takes without a screen change. */
  script?: ReactNode;
  children?: ReactNode;
}

/**
 * One take, staged as a card: how far through you are, the line to read, and what the
 * microphone is hearing while you say it. It replaced a paragraph of instructions, a list
 * of captured takes and a button that counted down in its own label -- four places to look,
 * none of which showed whether the microphone was actually working.
 *
 * The line to read is the point of it. "Speak naturally for seven seconds" asks somebody to
 * invent 18 words about nothing while a clock runs, which is how you get four seconds of
 * "um" and a take that fails MIN_SPEECH_SEC. Given words, people just read them.
 *
 * Recording, the card *is* the indicator: nothing else on the screen changes colour, so
 * there is never a question about whether the microphone is open.
 */
export function StageCard({ head, count, live, script, children, className, ...rest }: StageCardProps) {
  return (
    <Card
      bare
      className={cx(
        "my-4 grid gap-gutter pt-stage-t px-pad pb-stage-b",
        "transition-[border-color,background-color,box-shadow] duration-160 ease-plain",
        live
          ? // Active state, per the design: while recording the border goes fully opaque
            // and grows -- drawn as an inset ring so the card's contents do not reflow
            // mid-take -- over the faint accent tint.
            "border-accent stage-live-tint ring-2 ring-accent ring-inset"
          : CARD_TONE,
        className
      )}
      {...rest}
    >
      <div className="flex items-center justify-between gap-4">
        {head}
        <div className="flex flex-col items-end gap-hair">
          {/* The recording indicator: a solid dot and a mono label, held steady. No pulse
              -- a calm instrument states its state rather than blinking it, and the meter
              below already moves with the voice. */}
          {live ? (
            <span className="inline-flex items-center gap-nudge font-code text-count font-semibold uppercase tracking-label text-accent">
              <span className="w-1.5 h-1.5 rounded-full bg-accent flex-none" aria-hidden="true" />
              {t("enroll.live_badge")}
            </span>
          ) : null}
          <span
            className={cx(
              "font-code font-bold tabular-nums",
              // Counting down, the same slot carries the seconds left -- larger and accented,
              // because it is the one number that says when to stop talking.
              live
                ? "text-live-count tracking-normal text-accent"
                : "text-count uppercase tracking-label text-ink-soft"
            )}
            aria-hidden="true"
          >
            {count}
          </span>
        </div>
      </div>
      {script === undefined ? null : (
        // After the session code, the largest text in the app: it is read aloud, from arm's
        // length, while a clock runs. Body face at the read-aloud register -- this is a
        // sentence for a person, not an instrument label.
        <p
          className="m-0 text-script font-medium text-balance"
          aria-live="polite"
        >
          {script}
        </p>
      )}
      {children}
    </Card>
  );
}
